import {Grid, ThemeProvider} from "@mui/material";
import CategorySidebar from "./category-sidebar/CategorySidebar";
import React, {ChangeEvent, useCallback, useEffect, useMemo, useState} from "react";
import Box from "@mui/material/Box";
import CategorySelect from "./category-select/CategorySelect";
import DisplayWishes from "./display-wishes/DisplayWishes";
import {getCitiesWithCounts, getWishes} from "../../store/wish/ActionCreators";
import {RootState, useAppDispatch, useAppSelector} from "../../store";
import {WishSortOptions, WishStatus, WishType} from "../../store/wish/Helper";
import Button from "@mui/material/Button";
import {getCategories} from "../../store/category/ActionCreators";
import {whiteInputsTheme} from "../../util/theme/whiteInputsTheme";
import useProgressiveInput from "../../hooks/useProgressiveInput";
import {useTranslation} from "react-i18next";
import ReactGA from "react-ga4";
import PageTitle from "../shared/page-title/PageTitle";
import CitySidebar from "./city-sidebar/CitySidebar";
import CitySelect from "./city-select/CitySelect";

const Wishes = () => {
    const dispatch = useAppDispatch();
    const wishes = useAppSelector((state: RootState) => state.wishReducer.items);

    // ---- UI/filter state ----
    const [selectedCategory, setSelectedCategory] = useState<string>("all");
    const [selectedCity, setSelectedCity] = useState<string>("all");
    const [wishSorting, setWishSorting] = useState<string>(`${WishSortOptions.END_TIME},asc`);
    const [page, setPage] = useState(1);
    const [type, setType] = useState<WishType | undefined>(undefined);

    // search: tmp (input) vs debounced applied
    const [searchText, setSearchText] = useState("");
    const [tmpSearchText, setTmpSearchText] = useState("");

    const [scheduleDebouncedSearch] = useProgressiveInput((value: string) => {
        setPage(1);
        setSearchText(value);
        ReactGA.event("search", {search_term: value});
    }, 1000);

    const {t} = useTranslation();

    // ---- Fetch categories when type changes ----
    useEffect(() => {
        dispatch(getCategories({type, includeEmpty: false, sorted: true}));
        dispatch(getCitiesWithCounts({type, sorted: true}));
    }, [dispatch, type]);

    // ---- Centralized wishes fetching ----
    const params = useMemo(() => {
        return {
            category_id: selectedCategory === "all" ? undefined : selectedCategory,
            city: selectedCity === "all" ? undefined : selectedCity,
            limit: 6,
            page: page - 1,
            searchText,
            sorting: wishSorting,
            status: WishStatus.ACTIVE,
            type
        };
    }, [selectedCategory, page, searchText, wishSorting, type, selectedCity]);

    useEffect(() => {
        const loadMore = page > 1;
        dispatch(getWishes({...params, loadMore}));
    }, [dispatch, params, page]);

    // ---- Handlers that only mutate state ----
    const handleSortingChange = (value: WishSortOptions) => {
        setWishSorting(value);
        setPage(1);
    };

    const handleChangePage = (_event: ChangeEvent<unknown>, nextPage: number) => {
        setPage(nextPage);
    };

    const handleChangeSearchText = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setTmpSearchText(value);
        scheduleDebouncedSearch(value);
    };

    const applySearchText = (value: string) => {
        // Keep this for explicit "apply" actions (e.g. pressing Enter)
        setTmpSearchText(value);
        setPage(1);
        setSearchText(value);
        ReactGA.event("search", {search_term: value});
    };

    const handleChangeCategory = useCallback((id: string) => {
        const nextId = id.trim() || "all";
        setSelectedCategory(nextId);
        setPage(1);
    }, []);

    const handleChangeCity = useCallback((id: string) => {
        const nextId = id.trim() || "all";
        setSelectedCity(nextId);
        setPage(1);
    }, []);

    // ---- Derived UI flags ----
    const hasMore = Boolean(wishes?.data?.length) && page < (wishes?.total_pages ?? 1);

    return (
        <ThemeProvider theme={whiteInputsTheme}>
            <PageTitle title={t("wishes")} sx={{display: {md: "none"}, mb: "0.75em"}}/>
            <Grid container spacing={4}>
                <Grid item md={3} sx={{display: {xs: "none", md: "block"}}}>
                    <CategorySidebar selectedCategory={selectedCategory} handleChangeCategory={handleChangeCategory}/>
                    <CitySidebar selectedCity={selectedCity} handleChangeCity={handleChangeCity}/>
                </Grid>

                <Grid item xs={12} md={9}>
                    <Grid item xs={12} sx={{display: {xs: "flex", md: "none"}}} mb="1em">
                        <CategorySelect
                            selectedCategory={selectedCategory}
                            disableCategoriesWithoutWishes
                            handleChangeCategory={handleChangeCategory}
                        />
                    </Grid>
                    <Grid item xs={12} sx={{display: {xs: "flex", md: "none"}}} mb="1em">
                        <CitySelect
                            selectedCity={selectedCity}
                            handleChangeCity={handleChangeCity}
                        />
                    </Grid>

                    <DisplayWishes
                        wishSorting={wishSorting}
                        handleSortingChange={handleSortingChange}
                        searchText={tmpSearchText}
                        type={type}
                        setType={(next) => {
                            setType(next);
                            setPage(1);
                            setSelectedCategory("all")
                            setSelectedCity("all")
                        }}
                        handleChangeSearchText={handleChangeSearchText}
                        applySearchText={applySearchText}
                    />

                    {hasMore ? (
                        <Box textAlign="center" my="2em">
                            <Button variant="outlined" size="large"
                                    onClick={(e) => handleChangePage(e as any, page + 1)}>
                                {t("load_more")}
                            </Button>
                        </Box>
                    ) : null}
                </Grid>
            </Grid>
        </ThemeProvider>
    );
};

export default Wishes;
